// @flow
/* eslint-disable no-fallthrough  */

import { isCastSessionActive } from './CastUtils';
import type { State, Action, CastButtonState, SessionState } from './types';

export const initialState: State = {
  castButtonState: 'UNAVAILABLE',
  sessionState: 'CAST_SESSION_DISCONNECTED',
  deviceName: undefined,
  error: undefined,
  playingOnCastDevice: false,
  storeHasBeenSynced: false,
  sourceType: undefined,
  currentVideoProgress: undefined,
};

const mapCastState = (sessionState: SessionState): CastButtonState | void => {
  switch (sessionState) {
    case 'CAST_SESSION_STARTED':
    case 'CAST_SESSION_RESUMED':
      return 'CONNECTED';
    case 'CAST_SESSION_SUSPENDED':
    case 'CAST_SESSION_STARTING':
    case 'CAST_SESSION_RESUMING':
      return 'CONNECTING';
    case 'CAST_SESSION_START_FAILED':
    case 'CAST_SESSION_RESUME_FAILED':
    case 'CAST_SESSION_DISCONNECTED':
    case 'CAST_SESSION_ENDING':
    case 'CAST_SESSION_ENDED':
      return 'DISCONNECTED';
    default:
      console.error(`Unknown session state: ${sessionState}`);
      return undefined;
  }
};

let currentReceiverId: string;

const obtainCastButtonState = (
  currentCastButtonState: CastButtonState,
  newCastSessionState: SessionState,
): CastButtonState =>
  currentCastButtonState === 'UNAVAILABLE'
    ? mapCastState(newCastSessionState) || currentCastButtonState
    : currentCastButtonState;

const reducer = (state: State = initialState, action: Action): State => {
  // if (!action.isGoogleCastAction) return state;

  switch (action.type) {
    case 'CAST_BUTTON_STATE_CHANGED':
      return { ...state, castButtonState: action.buttonState };
    case 'CAST_PLAYING_ON_CAST_DEVICE':
      return { ...state, playingOnCastDevice: action.isPlaying, error: action.error };
    case 'CAST_SET_STORE':
      console.error(
        `CAST_SET_STORE action must have been handled in the global reducer not in the local one.
      Check src/dany-one/src/common/configureReducer.js.`,
      );
      return state;
    case 'CAST_STORE_HAS_BEEN_SYNCED':
      return { ...state, storeHasBeenSynced: action.payload };
    case 'CAST_SET_SOURCE_TYPE':
      return { ...state, sourceType: action.payload };
    case 'CAST_SET_VIDEO_PROGRESS':
      return {
        ...state,
        currentVideoProgress: action.progress,
      };
    // $FlowExpectedError
    case 'RESET_PLAYER_STREAM':
      return {
        ...state,
        currentVideoProgress: undefined,
      };
    // $FlowExpectedError
    case 'RESET_PLAYER':
      return {
        ...state,
        currentVideoProgress: undefined,
      };
    // $FlowExpectedError
    case 'CHANGE_ACTIVE_RECEIVER_SUCCESS': {
      const { receiverId } = action;
      let newState = state;
      if (currentReceiverId !== undefined && currentReceiverId !== receiverId) {
        newState = {
          ...state,
          storeHasBeenSynced: false,
          sourceType: undefined,
        };
      }
      currentReceiverId = receiverId;
      return newState;
    }
    // $FlowExpectedError
    case 'SIGN_OUT':
      return {
        ...state,
        storeHasBeenSynced: false,
        sourceType: undefined,
      };
    case 'CAST_SESSION_STARTED':
    case 'CAST_SESSION_STARTING':
    case 'CAST_SESSION_START_FAILED':
    case 'CAST_SESSION_RESUMED':
    case 'CAST_SESSION_RESUMING':
    case 'CAST_SESSION_RESUME_FAILED':
    case 'CAST_SESSION_SUSPENDED':
    case 'CAST_SESSION_DISCONNECTED':
    case 'CAST_SESSION_ENDING':
    case 'CAST_SESSION_ENDED': {
      const castButtonState = obtainCastButtonState(state.castButtonState, action.type);
      const sessionEnded = mapCastState(action.type) === 'DISCONNECTED';

      /* Case when connecting to a new chromecast device */
      const storeHasBeenSynced = sessionEnded === false && state.storeHasBeenSynced;

      return {
        ...state,
        castButtonState,
        storeHasBeenSynced,
        sessionState: action.type,
        deviceName: action.deviceName,
        error: action.error /* Set to false if session closes/fails for some reason. */,
        playingOnCastDevice: state.playingOnCastDevice && isCastSessionActive(action.type),
        sourceType: sessionEnded ? undefined : state.sourceType,
      };
    }
    default:
      return state;
  }
};

export default reducer;
